/**
 * Authentication Store for Soma Web Application
 * Manages authentication state using Zustand with enhanced security
 */

import { create } from 'zustand';
import { subscribeWithSelector, persist, createJSONStorage } from 'zustand/middleware';
import { authAPI, caAPI } from '../lib/api';
import { secureStorage, secureZustandStorage } from '../lib/storage';

// Create Zustand store for auth state
export const useAuthStore = create(
  persist(
    subscribeWithSelector((set, get) => ({
    // State
    isAuthenticated: false,
    user: null,
    caProfile: null,
    sessionExpiry: null,
    error: null,
    isInitialized: false,
    isLoading: false,

    // Actions
    setLoading: (loading) => set({ isLoading: loading }),
    
    setAuthState: (user, sessionExpiry, caProfile = null) => set({
      isAuthenticated: !!user,
      user,
      caProfile,
      sessionExpiry,
      error: null,
      isInitialized: true
    }),

    login: async (email, password) => {
      const { setLoading } = get();
      setLoading(true);
      
      try {
        const result = await authAPI.login({ email, password });
        
        if (result.success) {
          set({
            isAuthenticated: true,
            user: result.data.user,
            caProfile: result.data.caProfile || null,
            sessionExpiry: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
            error: null,
            isLoading: false
          });
        }
        
        return result;
      } catch (error) {
        set({
          error: error.message,
          isLoading: false
        });
        throw error;
      }
    },

    register: async (userData) => {
      const { setLoading } = get();
      setLoading(true);
      
      try {
        const result = await authAPI.register(userData);
        
        if (result.success) {
          set({
            isAuthenticated: true,
            user: result.data.user,
            caProfile: result.data.caProfile || null,
            sessionExpiry: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
            error: null,
            isLoading: false
          });
        }
        
        return result;
      } catch (error) {
        set({
          error: error.message,
          isLoading: false
        });
        throw error;
      }
    },

    registerCA: async (userData) => {
      const { setLoading } = get();
      setLoading(true);
      
      try {
        const result = await caAPI.register(userData);
        
        if (result.success) {
          set({
            isAuthenticated: true,
            user: result.data.user,
            caProfile: result.data.caProfile || null,
            sessionExpiry: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
            error: null,
            isLoading: false
          });
        }
        
        return result;
      } catch (error) {
        set({
          error: error.message,
          isLoading: false
        });
        throw error;
      }
    },

    logout: async () => {
      const { setLoading } = get();
      setLoading(true);
      
      try {
        // Add timeout to logout call to prevent hanging
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Logout timed out')), 2000);
        });

        await Promise.race([
          authAPI.logout(),
          timeoutPromise
        ]);
      } catch (error) {
        console.error('Logout error:', error);
      } finally {
        // Always clear local state and storage
        secureStorage.removeItem('soma_access_token');
        secureStorage.removeItem('soma_refresh_token');
        secureStorage.removeItem('soma_user_data');
        secureStorage.removeItem('auth-storage');
        
        set({
          isAuthenticated: false,
          user: null,
          sessionExpiry: null,
          error: null,
          isLoading: false,
          isInitialized: true // Ensure initialized is true so UI doesn't get stuck
        });
      }
    },

    validateSession: async () => {
      const { setLoading } = get();
      setLoading(true);
      
      try {
        const result = await authAPI.getCurrentUser();
        
        if (result.success && result.data) {
          set({
            isAuthenticated: true,
            user: result.data,
            sessionExpiry: Date.now() + (24 * 60 * 60 * 1000),
            error: null,
            isInitialized: true,
            isLoading: false
          });
          return true;
        } else {
          set({
            isAuthenticated: false,
            user: null,
            sessionExpiry: null,
            error: null,
            isInitialized: true,
            isLoading: false
          });
          return false;
        }
      } catch (error) {
        console.error('Session validation error:', error);
        // Clear invalid tokens on auth errors
        if (error.status === 401 || error.message?.includes('timeout')) {
          secureStorage.removeItem('soma_access_token');
          secureStorage.removeItem('soma_refresh_token');
          secureStorage.removeItem('soma_user_data');
        }
        set({
          isAuthenticated: false,
          user: null,
          sessionExpiry: null,
          error: error.message,
          isInitialized: true,
          isLoading: false
        });
        return false;
      }
    },

    initializeAuth: async () => {
      const { setLoading, isInitialized } = get();
      console.log('[Auth] initializeAuth called', { isInitialized });
      
      // Prevent multiple initialization attempts
      if (isInitialized) {
        return;
      }
      
      try {
        setLoading(true);
        
        // Check if we have stored tokens
        const token = secureStorage.getItem('soma_access_token');
        console.log('[Auth] Token found:', !!token);

        if (!token) {
          set({
            isAuthenticated: false,
            user: null,
            error: null
          });
          return;
        }
        
        // EMERGENCY FALLBACK: Use a timeout to prevent hanging
        const timeoutPromise = new Promise((resolve) => {
          setTimeout(() => {
            console.warn('[Auth] Initialization timed out');
            resolve({ success: false, data: null, error: 'timeout' });
          }, 3000); // 3 second timeout for auth initialization
        });
        
        console.log('[Auth] Starting validation race');
        // Race between API call and timeout
        const result = await Promise.race([
          authAPI.getCurrentUser(),
          timeoutPromise
        ]);
        console.log('[Auth] Validation result:', result);
        
        if (result.success && result.data) {
          const { user } = get();
          set({
            isAuthenticated: true,
            user: user ? { ...user, ...result.data } : result.data,
            sessionExpiry: Date.now() + (24 * 60 * 60 * 1000), // Extend session on successful init
            error: null
          });
        } else {
          // On timeout or failure, check if it's a critical auth failure
          // If it's just a timeout or server error, we keep the existing session (offline mode)
          if (result.error === 'timeout' || result.error === 'Service temporarily unavailable') {
            console.warn('[Auth] Auth check timed out or failed, preserving existing session');
            // We don't change auth state, just let it finish
          } else {
            console.warn('[Auth] Validation failed, clearing session');
            // Only clear if it's likely a valid auth failure
            secureStorage.removeItem('soma_access_token');
            secureStorage.removeItem('soma_refresh_token');
            secureStorage.removeItem('soma_user_data');
            set({
              isAuthenticated: false,
              user: null,
              error: null
            });
          }
        }
      } catch (error) {
        console.error('[Auth] Auth initialization error:', error);
        // Clear potentially invalid tokens on any error
        secureStorage.removeItem('soma_access_token');
        secureStorage.removeItem('soma_refresh_token');
        secureStorage.removeItem('soma_user_data');
        set({
          isAuthenticated: false,
          user: null,
          error: null // Don't show initialization errors to user
        });
      } finally {
        console.log('[Auth] Initialization complete, setting flags');
        // ALWAYS ensure we unblock the UI
        set({
          isInitialized: true,
          isLoading: false
        });
      }
    },

    clearError: () => set({ error: null }),

    updateUser: (userData) => set((state) => ({
      user: { ...state.user, ...userData }
    })),

    hasRole: (role) => {
      const { user } = get();
      return user?.roles?.includes(role) || false;
    },

    hasPermission: (permission) => {
      const { user } = get();
      return user?.permissions?.includes(permission) || false;
    },

    getSubscriptionStatus: () => {
      const { user } = get();
      return user?.subscription?.status || 'inactive';
    },

    hasActiveSubscription: () => {
      const status = get().getSubscriptionStatus();
      return status === 'active' || status === 'trialing';
    },

    // Cleanup function
    cleanup: () => {
      set({
        isAuthenticated: false,
        user: null,
        caProfile: null,
        sessionExpiry: null,
        error: null,
        isInitialized: false,
        isLoading: false
      });
    }
  })),
  {
    name: 'auth-storage',
    storage: createJSONStorage(() => secureZustandStorage),
    partialize: (state) => ({ 
      isAuthenticated: state.isAuthenticated, 
      user: state.user, 
      caProfile: state.caProfile,
      sessionExpiry: state.sessionExpiry 
    }),
  }
  )
);

// Export individual selectors for better performance
export const useAuth = useAuthStore;

export const useAuthActions = useAuthStore;